/**
 * Where a product can be bought, when it is not simply "at retail": the one
 * registry `purchaseChannel` (shared/src/utils/purchase-channel.ts) reads.
 *
 * Why (QA 2026-09-28, `trust-buy-buttons-open-wrong-destination`): every card
 * without a verified buy link fell back to an Amazon search, which assumes
 * Amazon sells the category. Prescription hearing aids are sold and fitted by
 * hearing care clinics: "Starkey Picasso" on amazon.ca returned a Picasso art
 * book, "Oticon More" chargers and wax guards. And a card the page itself
 * called "US-only, not sold in Canada" still had "Buy Now · Our pick".
 *
 * Three lists, all plain data:
 *  - CLINIC_LINES: products a professional sells and fits after an exam.
 *    Matched on brand AND product line (a brand word alone never decides:
 *    Oticon's chargers and wax guards are sold at retail). The page being a
 *    hearing-aid page stands in for the line, never for the brand, so an OTC
 *    device on the same page (Jabra Enhance, Sony CRE-C10, Lexie B2) stays
 *    retail.
 *  - MARKET_ONLY_LINES: product lines sold in some markets only. Outside them
 *    the product is not sold at all.
 *  - DTC_STORES: brands sold only on their own site (was shared/src/lib/dtc-brands.ts,
 *    which now reads this list).
 *
 * `markets` / `soldIn` are region codes ("CA", "UK", "US", "FR", ...);
 * omitted means every market. Pure data and tiny helpers, no imports: safe in
 * the client bundle and in the gate modules that import it.
 */

export type ClinicKind = "hearing";

export interface ClinicLine {
  kind: ClinicKind;
  /** Brand spellings, lower-case and accent-free, matched as whole words. */
  brands: readonly string[];
  /** Product lines of the brand sold through clinics, matched as whole words. */
  lines: readonly string[];
  /** Region codes where the rule applies; omitted = every market. */
  markets?: readonly string[];
  note?: string;
}

/**
 * Prescription hearing aids: sold and fitted by audiologists and hearing
 * instrument practitioners in every market we serve. Over-the-counter lines
 * are other brands (Jabra Enhance, Sony CRE, Lexie, Sennheiser All-Day Clear,
 * Eargo) and are deliberately absent.
 */
export const CLINIC_LINES: readonly ClinicLine[] = [
  {
    kind: "hearing",
    brands: ["phonak"],
    lines: [
      "audeo", "virto", "naida", "lumity", "paradise", "marvel", "infinio", "sphere", "bolero", "sky", "slim",
      "lyric", "belong", "cros", "venture", "nathos",
    ],
  },
  {
    kind: "hearing",
    brands: ["oticon"],
    lines: ["more", "intent", "real", "opn", "own", "zircon", "xceed", "play", "ruby", "siya", "jet", "dynamo", "zeal", "alta", "nera", "ria"],
  },
  {
    kind: "hearing",
    brands: ["signia"],
    lines: ["pure", "insio", "silk", "styletto", "motion", "active", "intuis", "prompt", "orion", "cros"],
  },
  {
    kind: "hearing",
    brands: ["widex"],
    lines: ["moment", "evoke", "smartric", "allure", "sheer", "beyond", "unique", "magnify", "energy", "enjoy", "dream"],
  },
  {
    kind: "hearing",
    brands: ["resound"],
    lines: ["omnia", "one", "nexia", "vivia", "linx", "enzo", "key", "savi", "quattro", "lift", "match"],
  },
  {
    kind: "hearing",
    brands: ["starkey"],
    lines: ["picasso", "evolv", "genesis", "livio", "edge", "muse", "signature", "arc", "omega", "halo", "synergy"],
  },
  {
    kind: "hearing",
    brands: ["unitron"],
    lines: ["vivante", "blu", "discover", "moxi", "stride", "insera", "smartfit", "tempus", "shine"],
  },
  {
    kind: "hearing",
    brands: ["bernafon"],
    lines: ["alpha", "viron", "zerena", "encanta", "leox", "juna", "saphira"],
  },
  {
    kind: "hearing",
    brands: ["rexton"],
    lines: ["reach", "bicore", "emerald", "motion", "inox", "core", "arena", "trax"],
  },
  {
    kind: "hearing",
    brands: ["beltone"],
    lines: ["serene", "imagine", "achieve", "amaze", "rely", "boost", "trust", "legend", "commence", "first", "ally"],
  },
  {
    kind: "hearing",
    brands: ["hansaton"],
    lines: ["sound", "beat", "jam", "aq", "scala", "velvet"],
  },
];

/** A clinic brand's own name, whatever the line: an accessory, a line we do not list yet. */
export const CLINIC_BRANDS: ReadonlySet<string> = new Set(CLINIC_LINES.flatMap((entry) => entry.brands));

/**
 * Words that make a clinic brand's product an accessory sold at retail: its
 * charger, domes, wax guards, batteries, TV streamer, remote microphone.
 * English and the languages of our markets.
 */
export const CLINIC_ACCESSORY_WORDS: ReadonlySet<string> = new Set([
  "charger", "chargers", "charging", "chargeur", "cargador", "ladegerat", "ladestation", "caricatore", "carregador",
  "dome", "domes", "wax", "cerumen", "cerustop", "guard", "guards", "filter", "filters", "filtre", "filtres",
  "battery", "batteries", "pile", "piles", "pila", "pilas", "batterie", "batterien", "batteria", "bateria",
  "case", "etui", "estuche", "hulle", "custodia", "remote", "telecommande", "fernbedienung", "telecomando",
  "adapter", "adaptateur", "adaptador", "streamer", "connectclip", "connector", "connecteur", "tv", "partnermic",
  // Not "mic" or "micro": "Widex Moment CIC Micro" is a hearing aid (QA
  // review, 2026-09-28), and so are the form factors CIC, IIC, ITE, ITC, RIC
  // and BTE, which never belong here.
  "roger", "microphone", "dryer", "drying", "dehumidifier", "deshumidificateur", "cleaning",
  "cleaner", "nettoyage", "limpieza", "reinigung", "kit", "cover", "covers", "sleeve", "sleeves", "tube", "tubes",
  "receiver", "receivers", "wire", "wires", "cord", "cable", "clip", "clips", "stand", "pouch", "spray", "wipes",
  "earmold", "earmould", "tips", "tip", "replacement", "compatible",
]);

/**
 * Page slugs of hearing-aid pages, in the languages of our markets. Supporting
 * evidence only: on such a page a clinic brand's product needs no listed line.
 */
export const HEARING_AID_PAGE_SLUGS: readonly string[] = [
  "hearing-aid", "aides-auditives", "aide-auditive", "appareils-auditifs", "appareil-auditif", "audifono",
  "audifonos", "horgerate", "hoergeraete", "apparecchi-acustici", "apparecchio-acustico", "aparelhos-auditivos",
  "aparelho-auditivo", "hoortoestel", "aparaty-sluchowe", "horapparat", "cymhorthion-clyw",
];

/** Words in a product title that say it is a hearing aid. */
export const HEARING_AID_TITLE_PHRASES: readonly string[] = [
  "hearing aid", "hearing aids", "aide auditive", "aides auditives", "appareil auditif", "appareils auditifs",
  "audifono", "audifonos", "horgerat", "horgerate", "apparecchio acustico", "apparecchi acustici",
  "aparelho auditivo", "aparelhos auditivos", "hoortoestel", "aparat sluchowy",
];

export interface MarketOnlyLine {
  brands: readonly string[];
  lines: readonly string[];
  /** The only markets the line is sold in. */
  soldIn: readonly string[];
  note?: string;
}

export const MARKET_ONLY_LINES: readonly MarketOnlyLine[] = [
  {
    brands: ["jabra"],
    lines: ["enhance"],
    soldIn: ["US"],
    note:
      "Jabra Enhance Select: over-the-counter hearing aids sold, fitted and serviced in the US only " +
      "(the en-CA behind-the-ear page itself called it 'US-only, not sold in Canada', 2026-09-28).",
  },
];

export interface DtcStore {
  url: string;
  /** Region codes the brand's store sells to; omitted = every market. */
  markets?: readonly string[];
}

/**
 * Direct-to-consumer brands that are NOT sold on Amazon. Sending their tiles
 * to an amazon.* search lands users on unrelated third-party results (the
 * Framework Laptop case), so these brands link to their official store
 * instead: link type 'other', no affiliate revenue (accepted trade-off).
 *
 * Keys are lowercase brand names, matched as the LEADING word(s) of the
 * product title (titles conventionally start with the brand), which keeps
 * common-noun collisions ("…Framesets", "Agile framework guide…") out.
 */
export const DTC_STORES: Readonly<Record<string, DtcStore>> = {
  framework: { url: "https://frame.work/" },
  system76: { url: "https://system76.com/" },
  fairphone: { url: "https://shop.fairphone.com/" },
};

/** The last two labels of a host: shop.fairphone.com → fairphone.com. */
function siteOf(host: string): string {
  const labels = host.toLowerCase().replace(/^www\./, "").replace(/\.$/, "").split(".").filter(Boolean);
  return labels.slice(-2).join(".");
}

function hostOf(url: string | null | undefined): string | null {
  if (typeof url !== "string" || !url.trim()) return null;
  try {
    return new URL(url.trim()).hostname;
  } catch {
    return null;
  }
}

/** Is this URL on the site of a brand sold only on its own site? Its home page is its store. */
export function isDtcStoreUrl(url: string | null | undefined): boolean {
  const host = hostOf(url);
  if (!host) return false;
  const site = siteOf(host);
  return Object.values(DTC_STORES).some((store) => {
    const storeHost = hostOf(store.url);
    return !!storeHost && siteOf(storeHost) === site;
  });
}
